import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"
import { datasetLonLat, locateDatasets } from './dataset-location';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Min/max of a numeric (or typed) array in a single pass — avoids
 * `Math.min(...arr)` / `Math.max(...arr)`, which throws "Maximum call stack
 * size exceeded" once `arr` exceeds the engine's argument-spread limit
 * (~100k). null/NaN/undefined entries are skipped, matching Math.min/Math.max's
 * intent on valid input. Empty/all-invalid input yields { min: Infinity, max: -Infinity },
 * same as Math.min()/Math.max() called with no arguments.
 */
export function minMax(arr) {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < arr.length; i++) {
    const v = arr[i];
    if (v === undefined || v === null || Number.isNaN(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return { min, max };
}

/** CSV text for the datasets list: one row per line, quotes doubled, lat/lon from the dataset location. */
export function datasetsToCsv(datasets) {
  const headers = ['Name', 'Method Type', 'QC Status', 'Created Date', 'Latitude', 'Longitude', 'Project ID'];
  const q = (cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`;
  const rows = datasets.map((dataset) => {
    const p = datasetLonLat(dataset);
    return [
      dataset.name,
      dataset.method_type,
      dataset.qc_status,
      new Date(dataset.created_at).toISOString(),
      p ? p[1] : '',
      p ? p[0] : '',
      dataset.project_id,
    ];
  });
  return [headers.join(','), ...rows.map((row) => row.map(q).join(','))].join('\n');
}

/**
 * Export datasets to CSV
 */
export function exportToCsv(datasets, filename = 'datasets.csv') {
  downloadFile(datasetsToCsv(datasets), filename, 'text/csv');
}

/** GeoJSON FeatureCollection of the datasets that have a location, plus how many were skipped. */
export function datasetsToGeoJson(datasets) {
  const { placed, skipped } = locateDatasets(datasets);
  const features = placed.map(({ dataset, lon, lat }) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [lon, lat] },
    properties: {
      name: dataset.name,
      method_type: dataset.method_type,
      qc_status: dataset.qc_status,
      created_at: dataset.created_at,
      dataset_id: dataset.id,
    },
  }));
  return { geojson: { type: 'FeatureCollection', features }, skipped };
}

/**
 * Export to GeoJSON. Datasets are placed by lib/dataset-location (lat/lon, georef or bbox);
 * returns { exported, skipped } so the caller can report datasets without a location.
 */
export function exportToGeoJson(datasets, filename = 'datasets.geojson') {
  const { geojson, skipped } = datasetsToGeoJson(datasets);
  if (geojson.features.length) downloadFile(JSON.stringify(geojson, null, 2), filename, 'application/geo+json');
  return { exported: geojson.features.length, skipped };
}

function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

export function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/** Evenly downsample an array to `cap` items. */
export function samplePoints(points, cap) {
  if (points.length <= cap) return points;
  const step = points.length / cap;
  const out = [];
  for (let i = 0; i < cap; i++) out.push(points[Math.floor(i * step)]);
  return out;
}
